import { EllipsisOutlined, RightOutlined } from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import {
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LinearScale,
  LineElement,
  PointElement,
  Title,
  Tooltip,
} from 'chart.js';
import { useMemo } from 'react';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';

import BookmarkIcon from '@/assets/icons/BookmarkIcon';
import TagIcon from '@/assets/icons/TagIcon';
import { CenterFlex, Flex, Space, VFlex } from '@/components/base';
import IconButton from '@/components/Button/IconButton';
import Spinner from '@/components/Common/Spinner';
import Empty from '@/components/Empty/Empty';
import Menu from '@/components/Menu/Menu';
import Modal from '@/components/Modal/Modal';
import { Stage } from '@/components/Stage/Stage';
import AMP_KEYS from '@/configs/amp-track-keys';
import queryKeys from '@/configs/react-query-keys';
import type { CreateChartPayload } from '@/entities/chart/api/types';
import {
  CHART_CALCULATION,
  CHART_FIELD,
  CHART_FIELD_ITEM_SELECTION_METHOD,
  CHART_GRAPH,
  CHART_METRIC,
  CHART_METRIC_VIEW_TYPE,
  CHART_TERM_TYPE,
  CHART_TERM_UNIT,
  CHART_TYPE,
} from '@/entities/chart/model/constants';
import useChartWordCloudItems from '@/features/(charts)/chart-renderer/ui/Single/use-chart-word-cloud-items';
import EditTopicModal from '@/features/Taxonomy/components/TaxonomyDetail/CreateTopicModal/EditTopicModal';
import StaticTaxonomyModal from '@/features/Taxonomy/components/TaxonomySetupModal/StaticTaxonomyModal';
import useColor from '@/hooks/domain/useColor';
import useWorkspaceRouter from '@/hooks/domain/useWorkspaceRouter';
import useModalState from '@/hooks/pure/useModalState';
import service from '@/services';
import insightExplorerService from '@/services/insight-explorer';
import useInsightExplorerStore from '@/stores/InsightExplorerStore';
import { customScrollbar } from '@/styles/style';
import { track } from '@/utils/track/track';

import useExplorerPaylaod from '../../hooks/useExplorerPaylaod';
import useWordCloudPayload from '../../hooks/useWordCloudPayload';
import {
  InsightExplorerByType,
  InsightExplorerByTypeMap,
  InsightExplorerFieldType,
  InsightExplorerFieldTypeMap,
} from '../../types/constants';
import { insightExplorerUtils } from '../../utils/insight-explorer.utils';
import BreadcrumbSection, { BreadCrumbItem } from './BreadcrumbSection';
import SubtopicSectionContainer from './SubtopicsSection/SubtopicSectionContainer';
import TotalVolumeTrendContainer from './TotalVolumeTrendSection/TotalVolumeTrendContainer';
import WordCloudSection from './WordCloudSection/WordCloudSection';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
);

const InsightExplorerContent = () => {
  const { field_type } = useParams();
  if (!field_type) throw new Error('field_type is required');
  const color = useColor();
  const { selectedNode, by, view } = useInsightExplorerStore();
  const { payload } = useExplorerPaylaod();
  const wordCloudPayload = useWordCloudPayload();
  const wordCloudChartPayload = useMemo(
    (): CreateChartPayload => ({
      name: 'Insight Explorer Wordcloud',
      type: CHART_TYPE.SINGLE_AXIS,
      historical: {
        frequency: null,
        value: false,
      },
      graph: CHART_GRAPH.WORD_CLOUD,
      field: {
        type: CHART_FIELD.FEEDBACK_KEYWORD,
        entire: true,
        candidates: [],
      },
      selected_field: {
        method: CHART_FIELD_ITEM_SELECTION_METHOD.TOP_50,
        items: [],
      },
      target: {
        is_compare: false,
        term: {
          type: CHART_TERM_TYPE.FIXED,
          unit: CHART_TERM_UNIT.DAY,
          start_date: wordCloudPayload.start_date as string,
          end_date: wordCloudPayload.end_date as string,
        },
        filter: Object.fromEntries(
          Object.entries(wordCloudPayload).filter(
            ([key]) => key !== 'start_date' && key !== 'end_date',
          ),
        ),
      },
      series: [
        {
          measure: {
            metric: CHART_METRIC.FEEDBACK,
            calculation: CHART_CALCULATION.COUNT,
          },
          options: {
            view_type: CHART_METRIC_VIEW_TYPE.COUNT,
          },
        },
      ],
    }),
    [wordCloudPayload],
  );

  const wr = useWorkspaceRouter();

  const viewSettingModalState = useModalState();

  const { data, isLoading } = useQuery({
    queryKey: [...queryKeys.insightExplorer.INSIGHT_EXPLORER_SELF, JSON.stringify(payload)],
    queryFn: () => insightExplorerService.getInsightExplorerTrend(payload),
    enabled:
      !!selectedNode &&
      !selectedNode?.exceptSelf &&
      insightExplorerUtils.getFieldFromNodeType(selectedNode.node_type) ===
        field_type,
  });

  const {
    data: wordCloudTable,
    isFetching: isWordCloudFetching,
    isLoading: isWordCloudLoading,
  } = useQuery({
    queryKey: [
      ...queryKeys.insightExplorer.INSIGHT_EXPLORER_WORD_CLOUD,
      selectedNode?.id,
      JSON.stringify(wordCloudChartPayload),
    ],
    queryFn: () => service.chart.fetchTable(wordCloudChartPayload),
    enabled:
      !!selectedNode &&
      !selectedNode?.exceptSelf &&
      insightExplorerUtils.getFieldFromNodeType(selectedNode.node_type) ===
        field_type,
  });
  const wordCloudData = useChartWordCloudItems({
    chart: wordCloudChartPayload,
    tableData: wordCloudTable,
  });

  const { data: childrenData, isLoading: isChildrenLoading } = useQuery({
    queryKey: [...queryKeys.insightExplorer.INSIGHT_EXPLORER_CHILDREN, payload],
    queryFn: () =>
      insightExplorerService.getInsightExplorerDistribution({
        by: selectedNode?.node_type || InsightExplorerByType.TAXONOMY,
        parent_id: selectedNode?.id || '',
        view: view,
        filter: payload.filter,
      }),
    enabled: !!selectedNode?.id,
  });

  const isSavedTopicEmpty = useMemo(() => {
    if (!selectedNode) return false;
    if (isLoading) return false;
    if (selectedNode.exceptSelf) {
      if (!selectedNode.children) return true;
      return selectedNode.children.length === 0;
    } else {
      return false;
    }
  }, [selectedNode, isLoading]);

  const isTaxonomy = by === InsightExplorerByType.TAXONOMY;

  return (
    <ContentContainer j="center">
      <VFlex
        padding="20px 24px 24px"
        g={20}
        style={{ maxWidth: 980, width: '100%', height: '100%' }}
      >
        <VFlex g={16}>
          <Flex j="space-between">
            <Flex a="center" g={8}>
              <Flex g={6}>
                <BreadCrumbItem isRoot>
                  {InsightExplorerFieldTypeMap.get(
                    field_type as InsightExplorerFieldType,
                  )}
                </BreadCrumbItem>
                <RightOutlined
                  style={{ fontSize: '10px', color: color.gray500 }}
                />

                <BreadcrumbSection />
              </Flex>
              {field_type === InsightExplorerFieldType.TAXONOMY && (
                <Menu
                  options={[
                    {
                      label: 'View Detail',
                      onClick: () => {
                        if (!selectedNode) return;
                        track(AMP_KEYS.INSIGHT_EXPLORER_VIEW_DETAIL_CLICK, {
                          node_type: InsightExplorerByTypeMap.get(
                            selectedNode.node_type,
                          ),
                          node_name: selectedNode.name,
                        });
                        viewSettingModalState.open();
                      },
                    },
                  ]}
                  triggerNode={
                    <IconButton icon={<EllipsisOutlined />} outlined={false} />
                  }
                />
              )}
            </Flex>

            {selectedNode &&
              selectedNode.node_type ===
                InsightExplorerByType.TAXONOMY_TOPIC && (
                <Modal
                  status={viewSettingModalState.status}
                  closeModal={viewSettingModalState.close}
                >
                  <EditTopicModal
                    alias={selectedNode?.id.toString() || ''}
                    refetch={() => {}}
                    static={true}
                    taxonomyAlias={
                      selectedNode?.path?.[0]?.value.toString() || ''
                    }
                  />
                </Modal>
              )}

            {selectedNode &&
              selectedNode.node_type === InsightExplorerByType.TAXONOMY && (
                <Modal
                  status={viewSettingModalState.status}
                  closeModal={viewSettingModalState.close}
                >
                  <StaticTaxonomyModal
                    taxonomyAlias={selectedNode?.id.toString() || ''}
                  />
                </Modal>
              )}
          </Flex>
        </VFlex>

        <Stage>
          <Stage.Scene status={isSavedTopicEmpty && isTaxonomy}>
            <Empty
              icon={<BookmarkIcon />}
              title="No saved topics yet"
              description="Create a topic to start uncovering insights."
              action={{
                onClick: () => {
                  if (!selectedNode) return;
                  wr.push(`/taxonomy/${selectedNode.id}/feedback`);
                },
                text: 'View saved topics',
                option: 'primary',
              }}
            />
          </Stage.Scene>
          <Stage.Scene status={isSavedTopicEmpty && !isTaxonomy}>
            <Empty
              icon={<TagIcon width={24} height={24} />}
              title="No tags yet"
              description="Create a tag to start uncovering insights."
              action={{
                onClick: () => {
                  if (!selectedNode) return;
                  wr.push(`/settings/tags`);
                },
                text: 'View tags',
                option: 'primary',
              }}
            />
          </Stage.Scene>
          <Stage.Scene status={isLoading}>
            <CenterFlex style={{ height: 400 }}>
              <Spinner />
            </CenterFlex>
          </Stage.Scene>
          <Stage.Scene status={data && !isLoading}>
            <VFlex g={16}>
              {!selectedNode?.exceptSelf && data && (
                <TotalVolumeTrendContainer
                  data={data}
                  field={field_type as InsightExplorerFieldType}
                  node_alias={selectedNode?.id || ''}
                />
              )}
              <SubtopicSectionContainer
                data={data}
                childrenData={childrenData}
                isChildrenLoading={isChildrenLoading}
              />
              {!selectedNode?.exceptSelf && (
                <WordCloudSection
                  words={wordCloudData}
                  isLoading={
                    isWordCloudLoading ||
                    isWordCloudFetching ||
                    isChildrenLoading
                  }
                />
              )}
            </VFlex>
          </Stage.Scene>
        </Stage>

        <Space h={64} />
      </VFlex>
    </ContentContainer>
  );
};

const ContentContainer = styled(Flex)`
  flex: 1;
  height: 100%;
  overflow: auto;
  background-color: ${(props) => props.theme.colors.gray150};
  ${customScrollbar};
`;

export default InsightExplorerContent;
